import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
  MeasuringStrategy,
} from "@dnd-kit/core";
import PitchView from "./pitchView/pitchView";
import PlayerList from "./playerList/playerList";
import ShareLineupButton, { useShareLineup } from "./shareLineupButton/shareLineupButton";
import FormationFilter from "./formationFilter/formationFilter";
import { FORMATIONS, DEFAULT_FORMATION } from "./formations";
import { findBestSlot } from "./findBestSlot";
import { lineupMXConfig } from "./configs/lineupMX.config";
import "./simulatorLineup.sass";
import WidgetContainer from "../reusablesComponents/containerWidget";

const measuring = {
  droppable: { strategy: MeasuringStrategy.Always },
};

/**
 * Contenido interno del simulador (compartido entre mobile y desktop)
 */
function SimulatorContent({
  title, accentColor, pitchColor, nameColor, lastNameColor, teamImage, backgroundTeam,
  slots, assignments, activeSlotId, activeSlot, draggingPlayer,
  formationKey, config, filledCount, positionFilter, players, showClubs, showClubToggle, showNationality, showNationalityToggle, selectionPath, showFormationFilter,
  handleSlotClick, handleClear, handleReset, handleFormationChange,
  handleAssign, handleAutoAssign, setPositionFilter, setShowClubs, setShowNationality, disableDrag, wrapperRef,
  onShare, isCapturing, isAndroidWebView,
}) {
  return (
    <div className="simulator-lineup" ref={wrapperRef}>
      <div className="simulator-lineup__header">
        <div className="simulator-lineup__header-top">
          <h3 className="simulator-lineup__title">{title}</h3>
          <span className="simulator-lineup__count">{filledCount} / 11</span>
        </div>
        <div className="simulator-lineup__controls">
          <div className="simulator-lineup__controls-actions">
            <button
              className="simulator-lineup__reset"
              onClick={handleReset}
              disabled={filledCount === 0}
              aria-label="Reiniciar alineación"
            >
              Reiniciar
            </button>

            {showClubToggle && (
              <button
                className={[
                  "simulator-lineup__club-toggle",
                  showClubs ? "simulator-lineup__club-toggle--active" : "simulator-lineup__club-toggle--inactive"
                ].filter(Boolean).join(" ")}
                onClick={() => setShowClubs(!showClubs)}
                aria-label={showClubs ? "Ocultar club" : "Ver club"}
                title={showClubs ? "Ocultar club" : "Ver club"}
              >
                Club
              </button>
            )}
            {showNationalityToggle && (
              <button
                className={[
                  "simulator-lineup__nationality-toggle",
                  showNationality ? "simulator-lineup__nationality-toggle--active" : "simulator-lineup__nationality-toggle--inactive"
                ].filter(Boolean).join(" ")}
                onClick={() => setShowNationality(!showNationality)}
                aria-label={showNationality ? "Ocultar país" : "Ver país"}
                title={showNationality ? "Ocultar país" : "Ver país"}
              >
                País
              </button>
            )}

            {filledCount === 11 && (
              <ShareLineupButton onShare={onShare} isCapturing={isCapturing} accentColor={accentColor} />
            )}
          </div>

          <div className="simulator-lineup__formationShare">
            {showFormationFilter && (
              <FormationFilter
                value={formationKey}
                onChange={handleFormationChange}
                accentColor={accentColor}
              />
            )}

            {filledCount === 11
              ? (
                <div className="simulator-lineup__complete"
                  style={accentColor ? { borderColor: accentColor, background: `${accentColor}18` } : undefined}
                >
                  <span className="simulator-lineup__complete-text">
                    {isAndroidWebView ? "¡Alineación completa!" : "¡Alineación completa! Comparte tu 11 ideal"}
                  </span>
                </div>
              )
              : <div className="simulator-lineup__complete"></div>
            }
          </div>

        </div>

      </div>

      <div className="simulator-lineup__body">
        <div className="simulator-lineup__pitch-col">
          <PitchView
            {...{ slots, assignments, activeSlotId, accentColor, pitchColor, nameColor, lastNameColor, draggingPlayer, teamImage, backgroundTeam, showClubs, showNationality, selectionPath }}
            onSlotClick={handleSlotClick}
            onClear={handleClear}
          />
        </div>
        <div className="simulator-lineup__list-col">
          <PlayerList
            {...{ players, assignments, activeSlot, accentColor, nameColor, lastNameColor, draggingPlayer, showClubs, showNationality, selectionPath }}
            positionFilter={positionFilter}
            onPositionFilterChange={setPositionFilter}
            onAssign={handleAssign}
            onAutoAssign={handleAutoAssign}
            onClear={handleClear}
            disableDrag={disableDrag}
          />
        </div>
      </div>

      <div className="simulator-lineup__footer">
        {filledCount === 11 && (
          <ShareLineupButton onShare={onShare} isCapturing={isCapturing} accentColor={accentColor} />
        )}
      </div>
    </div>
  );
}

export default function SimulatorLineup({ config = lineupMXConfig }) {
  const { players, title, accentColor, pitchColor, nameColor, lastNameColor, teamImage, backgroundTeam, showClubToggle, showNationalityToggle, selectionPath, showFormationFilter, defaultFormation } = config;

  const isAndroidWebView = useMemo(() => {
    const ua = navigator.userAgent;
    if (!/Android/.test(ua)) return false;
    return /wv/.test(ua) || (/Version\/\d/.test(ua) && !/SamsungBrowser/.test(ua));
  }, []);

  const [formationKey, setFormationKey] = useState(defaultFormation || DEFAULT_FORMATION);
  const [assignments, setAssignments] = useState({});
  const [activeSlotId, setActiveSlotId] = useState(null);
  const [draggingPlayer, setDraggingPlayer] = useState(null);
  const [positionFilter, setPositionFilter] = useState("all");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 780);
  const [showClubs, setShowClubs] = useState(false);
  const [showNationality, setShowNationality] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 780);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const formation = FORMATIONS[formationKey];
  const slots = formation.slots;
  const activeSlot = slots.find((s) => s.id === activeSlotId) || null;

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 10 } })
  );

  const handleFormationChange = useCallback((newKey) => {
    setAssignments({});
    setActiveSlotId(null);
    setFormationKey(newKey);
  }, []);

  const handleSlotClick = useCallback((slotId) => {
    setActiveSlotId((prev) => prev === slotId ? null : slotId);
  }, []);

  const handleAssign = useCallback((slotId, player) => {
    setAssignments((prev) => {
      const next = { ...prev };
      for (const [id, p] of Object.entries(next)) {
        if (p?.id === player.id) next[id] = null;
      }
      next[slotId] = player;
      return next;
    });
    setActiveSlotId(null);
  }, []);

  const handleClear = useCallback((slotId) => {
    setAssignments((prev) => ({ ...prev, [slotId]: null }));
    setActiveSlotId(null);
  }, []);

  const handleReset = useCallback(() => {
    setAssignments({});
    setActiveSlotId(null);
    setPositionFilter("all");
  }, []);

  const handleAutoAssign = useCallback((player) => {
    const bestSlot = findBestSlot(player, slots, assignments);
    if (!bestSlot) return false;
    handleAssign(bestSlot.id, player);
    return true;
  }, [slots, assignments, handleAssign]);

  const handleDragStart = useCallback(({ active }) => {
    const playerId = active.data.current?.playerId;
    if (playerId == null) return;
    const player = players.find((p) => p.id === playerId);
    setDraggingPlayer(player || null);
    setActiveSlotId(null);
  }, [players]);

  const handleDragEnd = useCallback(({ active, over }) => {
    setDraggingPlayer(null);
    if (!over) return;

    const playerId = active.data.current?.playerId;
    const sourceSlotId = active.data.current?.sourceSlotId; // presente si el drag viene de la cancha
    const slotId = over.data.current?.slotId;

    if (!playerId || !slotId) return;

    const player = players.find((p) => p.id === playerId);
    if (!player) return;

    // Si el origen es un slot de la cancha y el destino es otro slot → swap
    if (sourceSlotId && sourceSlotId !== slotId) {
      setAssignments((prev) => {
        const next = { ...prev };
        const targetPlayer = prev[slotId] || null;
        next[slotId] = player;
        next[sourceSlotId] = targetPlayer; // intercambia (o deja vacío si el destino estaba libre)
        return next;
      });
      return;
    }

    // Origen desde la lista de jugadores → comportamiento original
    handleAssign(slotId, player);
  }, [players, handleAssign]);

  const handleDragCancel = useCallback(() => setDraggingPlayer(null), []);

  useEffect(() => {
    if (!activeSlotId) return;
    const handleOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setActiveSlotId(null);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [activeSlotId]);

  const filledCount = useMemo(
    () => slots.filter((s) => assignments[s.id]).length,
    [slots, assignments]
  );

  // Share: lógica y canvas oculto centralizados — una sola instancia
  const { handleShare, isCapturing, hiddenCanvas } = useShareLineup({
    slots, assignments, formationKey, config,
  });

  // Props compartidas para SimulatorContent
  const contentProps = {
    title, accentColor, pitchColor, nameColor, lastNameColor, teamImage, backgroundTeam,
    slots, assignments, activeSlotId, activeSlot, formationKey, config,
    filledCount, positionFilter, players, showClubs, showClubToggle, showNationality, showNationalityToggle, selectionPath, showFormationFilter,
    handleSlotClick, handleClear, handleReset, handleFormationChange,
    handleAssign, handleAutoAssign, setPositionFilter, setShowClubs, setShowNationality, wrapperRef,
    onShare: handleShare, isCapturing, isAndroidWebView,
  };

  return (
    <WidgetContainer className="container__simulator-lineup-mx">
      {/* Canvas oculto para captura — montado una sola vez */}
      {hiddenCanvas}
      {isMobile ? (
        <SimulatorContent {...contentProps} draggingPlayer={null} disableDrag={true} />
      ) : (
        <DndContext
          {...{ sensors, measuring }}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SimulatorContent {...contentProps} draggingPlayer={draggingPlayer} disableDrag={false} />

          <DragOverlay dropAnimation={null}>
            {draggingPlayer ? (
              <div className="drag-overlay-pill">
                <span className="drag-overlay-pill__nombre">{String(draggingPlayer.firstName).toUpperCase()}</span>
                {" "}
                <span className="drag-overlay-pill__apellido">{String(draggingPlayer.lastName).toUpperCase()}</span>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </WidgetContainer>
  );
}
